import {memo, useCallback, useMemo} from 'react';

import styled from 'styled-components';
import {isUndefined} from 'lodash';

import {SkeletonLoader} from '~/shared/components/Loaders';
import {flipOnLTR} from '~/shared/theme/utils';
import DropDown from '~/shared/components/DropDown';
import {getLocalizationService} from '~/shared/services/localisationService';
import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {body14Bold} from '~/shared/theme/typography';

const Root = styled.div`
  position: absolute;
  top: ${({top}) => top || 0}px;
  ${flipOnLTR`
    left: ${({left}) => left || 0}px;
  `};
  z-index: 4;
`;

const CustomDropDown = styled(DropDown).attrs(({theme}) => ({
  styles: {
    control: provided => ({
      ...provided,
      borderWidth: 0,
      borderRadius: 0,
      borderColor: 'transparent',
      fontWeight: 'bold',
    }),
    placeholder: provided => ({
      ...provided,
      color: theme.userReport.filterDropDown.textColor,
    }),
    menu: provided => ({
      ...provided,
      top: -7,
      borderRadius: 12,
      borderWidth: 0,
      overflow: 'hidden',
    }),
    option: (provided, {isSelected}) => ({
      ...provided,
      fontWeight: isSelected ? 'bold' : 'normal',
    }),
  },
  isSearchable: false,
}))`
  width: 180px;
  ${body14Bold}

  svg {
    fill: ${({theme}) => theme.userReport.filterDropDown.arrowColor};
  }
`;

const FilterSelection = ({value, placeholder, filterOptions, onFilterChange, top, left, checkIfMoneycardIsCredit}) => {
  const {t} = getLocalizationService();
  const filterOptionsWithCustomNoOption = useMemo(
    () => [{clearFilter: true}, ...(filterOptions || [])],
    [filterOptions],
  );

  const getFilterLabel = useCallback(
    ({paymentMethod, cardSuffix, clearFilter: isClearFilterOption, paymentId, isTenbisCredit}) => {
      if (PaymentMethodTypes.GOOGLE_PAY === paymentMethod) {
        return 'Google Pay';
      }

      if (PaymentMethodTypes.APPLE_PAY === paymentMethod) {
        return t('applepay');
      }

      if (checkIfMoneycardIsCredit(paymentId) || isTenbisCredit) {
        return t('tenbis_credit');
      }

      if (isClearFilterOption) {
        return placeholder;
      }
      return `${t((paymentMethod || '').toLowerCase())} ${cardSuffix || ''}`;
    },
    [checkIfMoneycardIsCredit, placeholder, t],
  );

  const getFilterValue = useCallback(
    ({paymentMethod, cardSuffix, clearFilter: isClearFilterOption}) =>
      (isClearFilterOption ? '' : `${paymentMethod}_${cardSuffix}`),
    [],
  );

  return (
    <Root top={top} left={left}>
      <SkeletonLoader shouldShowLoader={isUndefined(filterOptionsWithCustomNoOption)} width={193} height={38}>
        <CustomDropDown
          name="filterSelection"
          value={value === '' ? filterOptionsWithCustomNoOption[0] : value}
          placeholder={placeholder}
          defaultValue={filterOptionsWithCustomNoOption[0]}
          options={filterOptionsWithCustomNoOption}
          onChange={onFilterChange}
          getOptionValue={getFilterValue}
          getOptionLabel={getFilterLabel}
        />
      </SkeletonLoader>
    </Root>
  );
};

export default memo(FilterSelection);
